import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import API from '../API';
import { useToast } from '../contexts/ToastContext';
import {
  getOrganizationEndDate,
  getOrganizationEndDateReason,
} from '../utils/organizationEndDate';

export const END_DATE_REASONS = [
  'Trial',
  'Churn',
  'Non-payment',
  'Migration',
  'Other',
];

const isoDate = (date) => date.toISOString().slice(0, 10);

const plusDaysIso = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return isoDate(date);
};

const errorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  error.message ||
  'Unknown error';

const SetEndDateModal = ({ open, organization, onClose, onSaved }) => {
  const { showToast } = useToast();
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const existing = organization ? getOrganizationEndDate(organization) : null;

  // Reset fields from the organization whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setDate(existing ? isoDate(existing) : '');
    setReason(getOrganizationEndDateReason(organization) || '');
    setDescription(organization?.endDateDescription || '');
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organization]);

  const handleClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  if (!open || !organization) return null;

  const handleReasonChange = (event) => {
    const next = event.target.value;
    setReason(next);
    // For trial, auto-fill +14 days if no date chosen yet.
    if (next === 'Trial' && !date) {
      setDate(plusDaysIso(14));
    }
  };

  const runSave = async (label, apiCall) => {
    setBusy(true);
    try {
      await apiCall();
      showToast(label);
      if (onSaved) await onSaved();
      onClose();
    } catch (error) {
      showToast(`Something went wrong: ${errorMessage(error)}`, { type: 'error' });
      setBusy(false);
    }
  };

  const handleSave = () =>
    runSave(`End-date set for ${organization.name}`, () =>
      API.updateOrganizationEndDate(organization.id, { date, reason, description })
    );

  const handleRemove = () =>
    runSave(`End-date removed for ${organization.name}`, () =>
      API.deleteOrganizationEndDate(organization.id)
    );

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Set end-date"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
          {existing ? 'Change end-date' : 'Set end-date'}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {organization?.name}
        </p>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="endDateReason"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Reason
            </label>
            <select
              id="endDateReason"
              value={reason}
              onChange={handleReasonChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">— Select reason —</option>
              {END_DATE_REASONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="endDateDescription"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Describe reason
            </label>
            <textarea
              id="endDateDescription"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          <div>
            <label
              htmlFor="endDateDate"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Date
            </label>
            <input
              id="endDateDate"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              For trial, the date is filled in automatically (+14 days) — still editable.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The tenant is automatically set to Paused once this date passes.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>

          <div className="flex items-center space-x-3">
            {existing && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900 disabled:opacity-50"
              >
                Remove end-date
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !date}
              className="px-4 py-2 text-sm font-medium bg-grafokett-blue text-white rounded-lg hover:bg-grafokett-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save end-date
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SetEndDateModal;
